/**
 * MODULE: _shared/frontend-shell/src/lib/agenticWorkspaceUrl.ts
 * PURPOSE: Build Traditional → Agentic Workspace deep-link URLs (no import from agentic-shell)
 * DEPENDENCIES: Vite VITE_AGENTIC_WORKSPACE_URL, workspacePeerOrigin, workspaceSessionHandoff
 * PUBLIC_API: getAgenticWorkspaceBaseUrl, buildAgenticMissionUrl, buildAgenticHomeUrl, redirectToAgentHome, agentPathForLegacyModule, redirectToAgentPath, AgenticLaunchDraft
 * SIDE_EFFECTS: May append #brazn_handoff when a Traditional session exists
 * AGENT SAFETY: 🟢 SAFE
 * FILE_SIZE: ~100 / 150 lines
 * RECENT: 2026-10-09 [2.2.2169] — Settings (except billing) map onto /agent/settings.
 * RECENT_PRIOR: 2026-09-15 [2.2.2138] — Hosted same-origin /agent; localhost keeps :5174
 * RECENT_PRIOR: 2026-07-30 [2.2.1844] — Attach session handoff for single-login switch
 */
import {
  resolveWorkspacePeerOrigin,
  type WorkspaceLocationHint,
} from '../../../lib/frontend/api/workspacePeerOrigin';
import { appendWorkspaceSessionHandoff } from '../../../lib/frontend/api/workspaceSessionHandoff';
export type AgenticLaunchDraft = {
  goal?: string;
  entityLabels?: string;
  outputType?: string;
  executionMode?: string;
  approvalPolicy?: string;
  scope?: string;
};

export type AgenticLaunchSource = 'play' | 'signal' | 'suggestion' | 'record' | 'workspace';

export function getAgenticWorkspaceBaseUrl(location?: WorkspaceLocationHint): string {
  return resolveWorkspacePeerOrigin({
    envOverride: import.meta.env.VITE_AGENTIC_WORKSPACE_URL as string | undefined,
    location,
    localFallback: 'http://localhost:5174',
  });
}

export function buildAgenticMissionSearch(
  draft: AgenticLaunchDraft,
  opts?: { from?: AgenticLaunchSource },
): string {
  const p = new URLSearchParams();
  if (draft.goal !== undefined) p.set('goal', draft.goal.trim());
  if (draft.entityLabels?.trim()) p.set('entities', draft.entityLabels.trim());
  if (draft.scope?.trim()) p.set('scope', draft.scope.trim());
  if (draft.outputType) p.set('output', draft.outputType);
  if (draft.executionMode) p.set('mode', draft.executionMode);
  if (draft.approvalPolicy) p.set('approval', draft.approvalPolicy);
  if (opts?.from) p.set('from', opts.from);
  const s = p.toString();
  return s ? `?${s}` : '';
}

/** Full URL to Agentic Home with optional mission composer prefill. */
export function buildAgenticMissionUrl(
  draft: AgenticLaunchDraft,
  opts?: { from?: AgenticLaunchSource },
): string {
  return appendWorkspaceSessionHandoff(
    `${getAgenticWorkspaceBaseUrl()}/agent${buildAgenticMissionSearch(draft, opts)}`,
  );
}

export function buildAgenticHomeUrl(opts?: { from?: AgenticLaunchSource }): string {
  const q = opts?.from ? `?from=${opts.from}` : '';
  return appendWorkspaceSessionHandoff(`${getAgenticWorkspaceBaseUrl()}/agent${q}`);
}

/** Leave the legacy shell for Agentic home. The durable URL has no from= parameter. */
export function redirectToAgentHome(): void {
  redirectToAgentPath('/agent');
}

/**
 * Map a legacy module URL onto an Agentic path.
 * Account, contact, and deal records keep their id. Other module pages open the conversation.
 */
export function agentPathForLegacyModule(pathname: string): string {
  const path = pathname.split('?')[0] ?? pathname;
  if (path.startsWith('/settings')) {
    if (path === '/settings/billing' || path.startsWith('/settings/billing/')) return '/settings/billing';
    if (/ai-usage|\/usage/.test(path)) return '/agent/settings#usage';
    if (/integration|email-account/.test(path)) return '/agent/settings#connectors';
    if (/session|security/.test(path)) return '/agent/settings#device';
    if (/workflow/.test(path)) return '/agent/builder/workflows';
    return '/agent/settings';
  }
  if (path === '/sales-profile' || path.startsWith('/sales-profile/')) return '/agent/settings';
  const account = path.match(/^\/(?:crm\/|companies\/|company\/)?accounts\/([^/]+)/)
    ?? path.match(/^\/(?:companies|company)\/([^/]+)/);
  if (account?.[1]) return `/agent/accounts/${encodeURIComponent(account[1])}`;
  const contact = path.match(/^\/(?:crm\/)?contacts\/([^/]+)/);
  if (contact?.[1]) return `/agent/contacts/${encodeURIComponent(contact[1])}`;
  const deal = path.match(/^\/(?:crm\/)?deals\/([^/]+)/);
  if (deal?.[1]) return `/agent/deals/${encodeURIComponent(deal[1])}`;
  const dashboard = path.match(/^\/dashboards\/([^/]+)/);
  if (dashboard?.[1]) return `/agent/dashboards/${encodeURIComponent(dashboard[1])}`;
  return '/agent';
}

/** Leave the legacy shell for a path under /agent. */
export function redirectToAgentPath(agentPath: string): void {
  const path = agentPath.startsWith('/agent') ? agentPath : '/agent';
  window.location.replace(
    appendWorkspaceSessionHandoff(`${getAgenticWorkspaceBaseUrl()}${path}`),
  );
}
